import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import {
  Radio, RefreshCw, Search, Phone, Mail, MapPin, FileText, FilePlus, ClipboardList, ChevronLeft, ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Quoted", "Won", "Lost"];
const STATUS_CLS = {
  New: "bg-slate-100 text-slate-600",
  Contacted: "bg-blue-50 text-blue-700",
  Quoted: "bg-amber-50 text-amber-700",
  Won: "bg-emerald-50 text-emerald-700",
  Lost: "bg-red-50 text-red-700",
};
const QUERY_TYPE_LABELS = { W: "Website", B: "Buy Lead", P: "Phone", WA: "WhatsApp" };
const PAGE_SIZE = 25;

export default function IndiaMartLeadsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [newToday, setNewToday] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [assignedTo, setAssignedTo] = useState("ALL");
  const [region, setRegion] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/indiamart/leads", {
        params: {
          q: q || undefined,
          status: status === "ALL" ? undefined : status,
          assigned_to: isAdmin && assignedTo !== "ALL" ? assignedTo : undefined,
          region: region || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          page,
          page_size: PAGE_SIZE,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setNewToday(data.new_today || 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load leads"); }
    finally { setLoading(false); }
  }, [q, status, assignedTo, region, dateFrom, dateTo, page, isAdmin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/indiamart");
        setLastSyncAt(data.status?.last_sync_at || null);
      } catch { /* non-admin users can't read settings — fine, just skip */ }
      try {
        const { data } = await api.get("/users");
        setUsers(data || []);
      } catch { /* sales users can't list users — assign dropdown just stays empty */ }
    })();
  }, []);

  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/indiamart/sync");
      toast.success(`Synced — ${data.new} new, ${data.duplicate} duplicate`);
      setLastSyncAt(new Date().toISOString());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const openDetail = async (row) => {
    setSelected(row);
    setDetailOpen(true);
    try {
      const { data } = await api.get(`/indiamart/leads/${row.id}`);
      setSelected(data);
    } catch { /* keep the row summary if the detail fetch fails */ }
  };

  const patchLead = async (id, patch) => {
    try {
      const { data } = await api.patch(`/indiamart/leads/${id}`, patch);
      setSelected(data);
      setItems((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="indiamart-leads-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">CRM · Leads</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Radio className="w-7 h-7 text-indigo-600" /> IndiaMART Leads
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Auto-pulled from your IndiaMART CRM. {lastSyncAt ? `Last sync ${new Date(lastSyncAt).toLocaleString()}` : "Not synced yet"}.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-center" data-testid="new-today-counter">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">New Today</div>
            <div className="text-xl font-extrabold mono text-indigo-700">{newToday}</div>
          </div>
          <Button onClick={syncNow} disabled={syncing} className="bg-blue-600 hover:bg-blue-700" data-testid="leads-sync-now-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="leads-filters">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-slate-500 font-normal">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input className="pl-8" placeholder="Name, company, mobile, email, product…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} data-testid="leads-search-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 font-normal">Status</Label>
          <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
            <SelectTrigger className="w-40 mt-1" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isAdmin && (
          <div>
            <Label className="text-xs text-slate-500 font-normal">Assigned To</Label>
            <Select value={assignedTo} onValueChange={(v) => { setPage(1); setAssignedTo(v); }}>
              <SelectTrigger className="w-48 mt-1" data-testid="leads-assigned-to-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Everyone</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs text-slate-500 font-normal">Region</Label>
          <Input className="w-36 mt-1" placeholder="e.g. Karnataka" value={region} onChange={(e) => { setPage(1); setRegion(e.target.value); }} data-testid="leads-region-filter" />
        </div>
        <div>
          <Label className="text-xs text-slate-500 font-normal">From</Label>
          <Input type="date" className="mt-1" value={dateFrom} onChange={(e) => { setPage(1); setDateFrom(e.target.value); }} />
        </div>
        <div>
          <Label className="text-xs text-slate-500 font-normal">To</Label>
          <Input type="date" className="mt-1" value={dateTo} onChange={(e) => { setPage(1); setDateTo(e.target.value); }} />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Company</th>
                <th className="text-left px-4 py-2.5">Mobile</th>
                <th className="text-left px-4 py-2.5">Product</th>
                <th className="text-left px-4 py-2.5">City</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Region</th>
                <th className="text-left px-4 py-2.5">Assigned To</th>
                <th className="text-left px-4 py-2.5">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!loading && items.length === 0 && (
                <tr><td colSpan={11} className="text-center text-slate-500 text-sm py-10" data-testid="no-leads">No IndiaMART leads yet.</td></tr>
              )}
              {items.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(l)} data-testid={`indiamart-lead-row-${l.id}`}>
                  <td className="px-4 py-2.5 text-xs text-slate-500 mono whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{l.sender_name || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{l.sender_company || "—"}</td>
                  <td className="px-4 py-2.5 mono text-xs">{l.sender_mobile || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{l.query_product_name || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{l.sender_city || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{QUERY_TYPE_LABELS[l.query_type] || l.query_type || "—"}</td>
                  <td className="px-4 py-2.5"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_CLS[l.status] || STATUS_CLS.New}`}>{l.status}</span></td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{l.assignment_region || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">
                    <div className="flex items-center gap-1.5">
                      {l.assigned_to || "Unassigned"}
                      {l.needs_manual_assignment && (
                        <span title="Needs manual assignment" data-testid={`lead-needs-manual-badge-${l.id}`}>
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-[11px] text-slate-400 max-w-[160px] truncate" title={l.assignment_reason || ""}>{l.assignment_reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
          <span>{total} lead{total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="leads-prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
            <span className="mono">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="leads-next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="lead-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {selected.sender_name || selected.sender_company || "Lead"}
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_CLS[selected.status] || STATUS_CLS.New}`}>{selected.status}</span>
                </SheetTitle>
                <SheetDescription>IndiaMART inquiry · {selected.external_id}</SheetDescription>
              </SheetHeader>

              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-[10px] uppercase text-slate-500">Company</div><div>{selected.sender_company || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Product</div><div>{selected.query_product_name || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Mobile</div><div className="flex items-center gap-1"><Phone className="w-3 h-3" />{selected.sender_mobile || "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Email</div><div className="flex items-center gap-1"><Mail className="w-3 h-3" />{selected.sender_email || "—"}</div></div>
                  <div className="col-span-2"><div className="text-[10px] uppercase text-slate-500">Address</div><div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[selected.sender_address, selected.sender_city, selected.sender_state, selected.sender_pincode].filter(Boolean).join(", ") || "—"}</div></div>
                </div>

                {selected.query_message && (
                  <div className="border-l-2 border-indigo-300 pl-3 text-sm italic text-slate-600">{selected.query_message}</div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500 font-normal">Status</Label>
                    <Select value={selected.status} onValueChange={(v) => patchLead(selected.id, { status: v })}>
                      <SelectTrigger className="mt-1" data-testid="lead-detail-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 font-normal">Assigned To</Label>
                    <Select value={selected.assigned_to || "__none__"} onValueChange={(v) => patchLead(selected.id, { assigned_to: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="mt-1" data-testid="lead-detail-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(selected.assignment_reason || selected.assignment_region || selected.needs_manual_assignment) && (
                  <div className="border border-slate-200 rounded-md p-3 text-xs space-y-1" data-testid="lead-detail-assignment-info">
                    {selected.needs_manual_assignment && (
                      <div className="flex items-center gap-1.5 text-amber-700 font-medium">
                        <AlertTriangle className="w-3.5 h-3.5" /> Needs manual assignment
                      </div>
                    )}
                    {selected.assignment_region && <div className="text-slate-600">Region: {selected.assignment_region}</div>}
                    {selected.assignment_reason && <div className="text-slate-500">{selected.assignment_reason}</div>}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-1" data-testid="lead-detail-actions">
                  <Button size="sm" variant="outline" onClick={() => nav("/raise-quotation")}><FileText className="w-3.5 h-3.5 mr-1" /> Create Quotation</Button>
                  <Button size="sm" variant="outline" onClick={() => nav("/raise-transfer-order")}><FilePlus className="w-3.5 h-3.5 mr-1" /> Raise Transfer Order</Button>
                  {selected.task_id && (
                    <Button size="sm" variant="outline" onClick={() => nav("/sales-report/tasks")}><ClipboardList className="w-3.5 h-3.5 mr-1" /> Open Task</Button>
                  )}
                </div>

                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-1.5">Raw Payload</div>
                  <pre className="bg-slate-50 border border-slate-200 rounded-md p-3 text-[11px] mono overflow-x-auto max-h-64 overflow-y-auto" data-testid="lead-raw-payload">
                    {JSON.stringify(selected.raw_payload || {}, null, 2)}
                  </pre>
                </div>
              </div>
              <SheetFooter className="mt-6" />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
